<script setup>
import { ref, defineProps, watch, onMounted, onUnmounted, defineEmits } from 'vue';
import axios from 'axios';
import Stepper from '@/components/Stepper.vue';
import USDCLogo  from '@/assets/images/usdc.png';
import { useLayoutComponents } from '@/composables/layout';
import { createNewEvalRun } from '@/services/evalService';
const { ButtonLoading } = useLayoutComponents();
import { openWalletModal, walletStatus, sendUSDC, platformReceipt, sellerReceipt } from '../../plugins/wagmi/connect';
import { useRouter } from 'vue-router';

const router = useRouter();
const emit = defineEmits(['payment-success', 'finish']);
const props = defineProps({
    buyActive: {
        type: Object,
        required: true
    },
    paymentAmount: {
        type: Number,
        required: true
    },
    creditsUsed : {
        type: Number,
        required: false,
        default: 0
    },
    userInfo: {
        type: Object,
        required: true
    },
    listingId: {
        type: String,
        required: true
    },
    isBid: {
        type: Boolean,
        required: false,
        default: false
    },
    isEvalRun: {
        type: Boolean,
        required: false,
        default: false
    },
    modelResponseFile: {
        type: String,
        required: false,
        default: null
    },
    version: {
        type: Number,
        required: false,
        default: null
    }
});

const step = ref(1);
const sale = ref(false);
const buyNowClicked = ref(false);
const paymentAmount = ref(props.paymentAmount);
const feeAmout = ref(0)
const sellerAmount = ref(0)
const currentAmount = ref(0)
const steps = ref([{name: 'Payment to Seller',text: 'Make payment to Seller'}])

const handleNextStep = async () => {
    if (!walletStatus.value) {
        openWalletModal();
        return;
    }
    try {
        if(buyNowClicked.value) return;
        buyNowClicked.value = true;
        const txHash = await sendUSDC(paymentAmount.value.toString(), props.buyActive.receipt_address, props.buyActive.fee);
        if (txHash) {
            if(props.isBid){
                await confirmBid(sellerReceipt.value);
            } else if(props.isFinishSale) {
                await finishSale( sellerReceipt.value);
            } else if(props.isEvalRun) {
                createEvalRun(paymentAmount.value, sellerReceipt.value);
            }
             else {
                await createSale( sellerReceipt.value);
            }
        } else {
            step.value = 1;
        }
    } catch (error) {
        console.error('Purchase failed:', error);
    } finally {
        buyNowClicked.value = false;
    }
}

const createSale = async (tx) => {
    if(!tx) return;
    step.value = steps.value.length;
    const payments = [
        {
            method: 'wallet',
            amount: paymentAmount.value
        }
    ]
    if(props.creditsUsed > 0) {
        payments.push({
            method: 'credits',
            amount: props.creditsUsed
        })
    }
    const values = {
        listing_id: props.listingId,
        buyer_id: props.userInfo.id || null,
        final_price: paymentAmount.value,
        currency: 'USDC',
        tx_hash: tx?.transactionHash || 'void_tx',
        payments: payments
    }

    const response = await axios.post('/api/sales', values);
    if (response?.data?.paid || response?.data?.tx_hash === 'void_tx') {
        sale.value = true;
        emit('payment-success');
    } else {
        alert('Purchase didn\'t work, please try again later')
    }
}

const confirmBid = async (tx) => {
    const values = {
        listing_id: props.listingId,
        buyer_id: props.userInfo.id || null,
        note: 'Payment for bid',
        receipt_address: props.buyActive.receipt_address,
        tx_hash: tx?.transactionHash || 'void_tx',
        bid_amount: paymentAmount.value,
        payments: [
            {
                method: 'wallet',
                amount: paymentAmount.value
            }
        ]
    }
    const response = await axios.post(`/api/bids/${props.buyActive.id}/confirm-payment`, values);
    if (response?.data?.bid?.status === 'PAID') {
        sale.value = true;
    } else {
        alert('Purchase didn\'t work, please try again later')
    }
}

const finishSale = async (tx) => {
    const values = {
        sale_id: props.buyActive.id,
        final_price: paymentAmount.value,
        currency: 'USDC',
        tx_hash: tx?.transactionHash || 'void_tx',
        payments: [{
            method: 'wallet',
            amount: paymentAmount.value
        }],
        receipt_address: props.buyActive.receipt_address
    }

    const response = await axios.post('/api/sales/finish-payment', values);
    if (response?.data?.paid || response?.data?.tx_hash === 'void_tx') {
        sale.value = true;
        emit('payment-success');
    } else {
        alert('Purchase didn\'t work, please try again later')
    }
}

const createEvalRun = async (amountPayment, txHash) => {
    try {
        const payments = [{ method: 'wallet', amount: paymentAmount.value }]
        if(props.creditsUsed > 0) {
            payments.push({
                method: 'credits',
                amount: props.creditsUsed
            })
        }
        const response = await createNewEvalRun({
            eval_id: props.buyActive?.datasets[0]?.dataset?.eval_id,
            dataset_id: props.buyActive?.datasets[0]?.dataset_id,
            listing_id: props.buyActive?.id,
            model_response_file: props.modelResponseFile,
            buyer_id: props.userInfo.id,
            seller_id: props.buyActive.datasets[0].dataset.seller.user.id,
            tx_hash: txHash ? txHash.transactionHash : 'void_tx',
            receipt_address: props.buyActive.receipt_address,
            final_price: paymentAmount.value,
            currency: 'USD',
            status: 'running',
            payments: payments,
            fee_amount: feeAmout.value,
            net_amount: paymentAmount.value - feeAmout.value,
            version: props.version
        });
        if (response?.evalRun?.sale?.paid || response?.evalRun?.sale?.tx_hash === 'void_tx')  {
            step.value = steps.value.length;
            sale.value = true;
            emit('payment-success');
        } else {
            alert('Purchase didn\'t work, please try again later');
        }
    } catch (error) {
        console.error('Failed to create eval run:', error);
        alert('Failed to create eval run');
    }
};

const goToDownload = () => {
    setTimeout(() => {
        const url = props.isEvalRun ? `/creator-profile?page=eval-results&subpage=eval-results` : `/creator-profile?page=purchases`;
        router.replace(url);
        
    }, 500);
}
const formatPrice = (price) => {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

watch(platformReceipt, (newVal) => {
    if(newVal) {
        step.value = 2
        currentAmount.value = sellerAmount.value
    }
})

watch(sellerReceipt, (newVal) => {
    if(newVal) {
        step.value = 3
        currentAmount.value = paymentAmount.value
    }
})

onMounted(() => {
    feeAmout.value  = props.buyActive.fee > 0 ? (Number(paymentAmount.value) * Number(props.buyActive.fee)) / 100 : 0;
    sellerAmount.value = Number(paymentAmount.value) - Number(feeAmout.value);
    if(props.buyActive.fee > 0) {
        currentAmount.value = feeAmout.value
        steps.value.unshift({
            name: 'Platform Fee',
            text: 'Pay Platform Fee',
        });
    } else {
        currentAmount.value = paymentAmount.value
    }
    steps.value.push({
        name: props.isEvalRun ? 'Result' : 'Download',
        text: '',
    });
})

onUnmounted(() => {
    platformReceipt.value = null;
    sellerReceipt.value = null;
})
</script>

<template>
    <div class="p-2">
        <!-- Stepper -->
        <div class="flex justify-center p-2">
            <Stepper :steps="steps.map(step => step.name)" :currentStep="step" />
        </div>
        
        <div class="divider-line"></div>
        <!-- Summary -->
        <div>
            <!-- Summary Content -->
            <div class="p-4 flex flex-col gap-2">
                <div class="text-lg font-bold">Summary</div>
                <div class="flex justify-between">
                    <div class="text-white/60">Payment to Seller</div>
                    <div>
                        <div class="flex items-end gap-2">
                            <div class="text-xs border border-green-500/50 rounded-full ps-3 pe-2 py-1 text-green-500" v-if="sellerReceipt">Paid<i class="uil uil-check"></i></div>
                            <div class="font-semibold" :class="{ 'text-green-500 line-through': sellerReceipt }">$ {{ formatPrice(sellerAmount) }}</div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-between" v-if="props.buyActive.fee > 0">
                    <div class="text-white/60">Platform Fee({{ props.buyActive.fee }}%)</div>
                    <div class="flex items-end gap-2">
                        <div class="text-xs border border-green-500/50 rounded-full ps-3 pe-2 py-1 text-green-500" v-if="platformReceipt">Paid<i class="uil uil-check"></i></div>
                        <div class="font-semibold" :class="{ 'text-green-500 line-through': platformReceipt }">$ {{ formatPrice(feeAmout) }}</div>
                    </div>
                </div>
            </div>

            <div class="divider-line"></div>
            <!-- Select Content -->
            <div class="flex justify-between p-4">
                <div class="text-lg font-bold">Total</div>
                <div class="font-semibold">$ {{ formatPrice(paymentAmount) }}</div>
            </div>
            <Transition name="slide-up" mode="out-in">
                <div v-if="step <= steps.length - 1" key="payment-instructions" class="h-full overflow-y-auto overflow-x-hidden">
                    <!-- Payment instructions -->
                    <div class="px-4 py-2 grid grid-cols-3 gap-4">
                        <div class="payment-method-card col-span-1">
                            <div class="pb-2">
                                <p class="text-lg">USDC</p>
                                <p class="text-xs">Save fees, requires a wallet.</p>
                            </div>
                            <div class="flex justify-center items-center">
                                <img :src="USDCLogo" alt="USDC" class="w-14">
                            </div>
                        </div>
                        <div class="col-span-2 w-2/3 flex flex-col gap-2">
                            <div class="text-echoes-primary">Step {{ step }} of {{ steps.length }}</div>
                            <div class="text-xl font-semibold">{{ steps[step - 1].text }}</div>
                            <div class="text-white/60">Transfer {{ formatPrice(currentAmount) }} USDC to {{ step === 1 && props.buyActive.fee > 0 ? 'Portex' : 'Seller' }} to activate your order.</div>
                        </div>
                    </div>

                    <!-- Button Actions -->
                    <div class="px-4 py-2 flex justify-end mb-2">
                        <div @click="handleNextStep" :disabled="buyNowClicked" 
                        :class="[buyNowClicked ? 'opacity-50 cursor-not-allowed' : 'opacity-100 cursor-pointer hover:bg-echoes-primary/80']"
                        class="px-4 py-2 rounded-lg bg-echoes-primary text-white transition-colors duration-200 ease-in-out">
                        <div v-if="!buyNowClicked">
                            {{ walletStatus ? 'Confirm Payment' : 'Connect Wallet' }}
                        </div>
                        <ButtonLoading v-else size="sm" text="Processing Payment..." />
                    </div>
                </div>
                </div>

                <!-- Sale Success -->
                <div v-else key="sale-success" class="h-full overflow-y-auto overflow-x-hidden">
                    <div class="px-4 py-2 grid grid-cols-3 gap-4">
                        <div class="col-span-1 flex justify-center items-center">
                            <div class="w-16 h-16 bg-emerald-300/50 rounded-full flex items-center justify-center mx-auto mb-4" v-if="sale">
                                <svg class="w-8 h-8 text-echoes-background-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                            </div>
                            <div class="w-16 h-16 bg-red-300/50 rounded-full flex items-center justify-center mx-auto mb-4" v-else>
                                <svg class="w-8 h-8 text-echoes-background-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                        </div>
                        <div class="col-span-2 w-2/3 flex flex-col gap-2">
                            <div class="text-echoes-primary">Step {{ step }} of {{ steps.length }}</div>
                            <div class="text-2xl font-bold">{{ sale ? 'Purchase Successful!' : 'Awaiting confirmation' }}</div>
                            <div class="text-white/60">{{ sale ? 'We\'ve confirmed your purchase.' : 'We\'re waiting for the transaction to be confirmed. This may take a few minutes.' }}</div>
                            
                            <div class="p-4 flex justify-center">
                                <ButtonLoading v-if="!sale" size="sm" text="Confirming Payment..." />
                                <div v-else class="text-xl px-4 py-2 rounded-lg bg-gradient-to-r text-sm from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer" @click="goToDownload">
                                    {{ props.isEvalRun ? 'View Result' : 'Download Now' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.divider-line {
    @apply w-full h-px bg-zinc-700;
}
.payment-method-card {
    @apply border-2 border-gray-600/50 rounded-xl px-4 pt-2 pb-4 text-center space-y-2 shadow-sm shadow-black bg-echoes-background-black/20;
}

/* Slide-up transition (para combinar com o comportamento do Checkout) */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden !important;
}

.slide-up-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.slide-up-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.slide-up-enter-active .overflow-y-auto,
.slide-up-leave-active .overflow-y-auto {
    scroll-behavior: smooth;
}
</style>